import { z } from "zod";

// The machine-readable declaration of a Product type's Variant attribute axes, stored in
// `product_types.variant_schema` (ADR-0054 §1) — the data-driven counterpart of the
// code-side `PillowVariantAttributes` (the pillow declares `shape` + `size_code` enums +
// an `size_cm` int). Each axis names its `type` and, for an enum, its allowed `values`.
//
// This is the contract the admin Katalog tab (#404) validates a variant `attributes`
// edit against: a non-conforming payload (an unknown/typo'd axis, a missing axis, or an
// out-of-enum value) is rejected at WRITE time with a 400, rather than persisting and
// then throwing for a *buyer* in `resolveVariant` on the next commit. Because it is
// schema-driven, a per-variant axis added later (e.g. #406's shipping-size class) is
// validated with no code change — the new axis is just declared in `variant_schema`.
export const VariantAxisSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("enum"),
    values: z.array(z.union([z.string(), z.number()])).min(1),
  }),
  z.object({ type: z.literal("int") }),
  z.object({ type: z.literal("string") }),
]);
export type VariantAxis = z.infer<typeof VariantAxisSchema>;

export const VariantSchemaDefinition = z.object({
  attributes: z.record(z.string(), VariantAxisSchema),
});
export type VariantSchemaDefinition = z.infer<typeof VariantSchemaDefinition>;

export type VariantAttributesValidation = { ok: true } | { ok: false; message: string };

// Validate a variant `attributes` object against a type's declared `variant_schema`
// (generic — no per-type hardcoding). Every declared axis must be present AND conform to
// its declared type/enum; an undeclared axis is rejected (an admin typo). A
// `variant_schema` that is not machine-readable (a descriptive-only blob) cannot gate an
// edit, so it fails OPEN — the edit is allowed, a server-config concern rather than the
// operator's fault. Messages are PL (rendered directly in the admin console).
export function validateVariantAttributes(
  rawSchema: unknown,
  attributes: Record<string, unknown>,
): VariantAttributesValidation {
  const parsed = VariantSchemaDefinition.safeParse(rawSchema);
  if (!parsed.success) return { ok: true };
  const axes = parsed.data.attributes;

  for (const key of Object.keys(attributes)) {
    if (!(key in axes)) {
      return { ok: false, message: `Nieznany atrybut: „${key}”.` };
    }
  }

  for (const [name, axis] of Object.entries(axes)) {
    if (!(name in attributes)) {
      return { ok: false, message: `Brak wymaganego atrybutu: „${name}”.` };
    }
    const value = attributes[name];
    if (axis.type === "enum") {
      if (!axis.values.includes(value as string | number)) {
        return { ok: false, message: `Niedozwolona wartość atrybutu „${name}”.` };
      }
    } else if (axis.type === "int") {
      if (typeof value !== "number" || !Number.isInteger(value)) {
        return { ok: false, message: `Atrybut „${name}” musi być liczbą całkowitą.` };
      }
    } else if (axis.type === "string") {
      if (typeof value !== "string") {
        return { ok: false, message: `Atrybut „${name}” musi być tekstem.` };
      }
    }
  }

  return { ok: true };
}
